/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {SegmentApiService, SegmentModel} from './segment-api.service';
import {SegmentCopyModalComponent} from './segment-copy-modal.component';
import {SegmentItemPageComponent} from './segment-item-page.component';
import {SegmentRoutePageComponent} from './segment-route-page.component';

export function segmentStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(SegmentApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const rh = adnRouterHelper.createRouterHelper('segments.segment', SegmentApiService, 'app.dat-seg', 'app');
  const copyConfig = {
    modalComponent: SegmentCopyModalComponent,
    stateUrl: 'app.dat-seg.segments.segment',
    lcase: 'segment',
    ucase: 'Segment'
  };

  states.add(rh.listPath(), rh.list({
    data: {app: ['ADVERTISING', 'DATA']},
    resolve: {listReadOnly: () => false},
    views: {
      'content@app': {component: SegmentRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {
        component: ListDefaultActionsComponent,
        bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}
      },
      'title@app': stateTitleView()
    }
  }));

  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      segmentPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          const model: SegmentModel = {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []};
          return new SignalAdminResourcePageState(model, true, false, api, 'Segment', 'name', copyConfig);
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, false, api, 'Segment', 'name', copyConfig));
      },
      model: function(segmentPage: SignalAdminResourcePageState<SegmentModel>) {
        return segmentPage.model();
      }
    },
    views: {
      'content@app': {component: SegmentItemPageComponent, bindings: {state: 'segmentPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'segmentPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'segmentPage'}}
    }
  }));

  return states.build();
}
